import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_school-hub-1220/artifacts/eggcq1ix_302319671_479406710859175_2268986965834108785_n.jpg";

export default function RegistroDocente() {
  const { config } = useAuth();
  const [form, setForm] = useState({
    nombre: "", email: "", password: "", telefono: "", cedula: "", titulo: "",
    materias: "", mensaje: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) return setError("La contraseña debe tener al menos 6 caracteres");
    setBusy(true);
    try {
      await api.post("/registro-docente", form);
      setListo(true);
    } catch (err) {
      setError(apiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-5">
          <img src={LOGO} alt={config.nombre} className="h-11 w-auto" />
          <div>
            <p className="font-display text-base font-extrabold tracking-tight">{config.nombre}</p>
            <p className="text-xs text-muted-foreground">
              Registro de docentes · {config.anio_lectivo}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        {listo ? (
          <div className="rise rounded-lg border border-primary/30 bg-primary/5 p-10 text-center"
               data-testid="registro-docente-exitoso">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-5 font-display text-3xl font-extrabold tracking-tight">¡Solicitud enviada!</h1>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
              La coordinación revisará su solicitud. Cuando sea aprobada recibirá un correo y podrá
              entrar con el correo y la contraseña que acaba de registrar.
            </p>
            <Button asChild className="mt-8 rounded-full">
              <Link to="/login" data-testid="ir-a-login-docente">Ir al inicio de sesión</Link>
            </Button>
          </div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Nuevo acceso · Docentes
            </p>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
              Cree su cuenta del portal de docentes
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Complete el formulario y la coordinación le asignará sus materias. Luego podrá publicar
              calificaciones, marcar asistencia, subir tareas y enviar su planificación.
            </p>

            <form onSubmit={enviar} className="mt-10 grid gap-5 sm:grid-cols-2"
                  data-testid="form-registro-docente">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre completo</Label>
                <Input value={form.nombre} required data-testid="docente-nombre"
                       onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Correo electrónico</Label>
                <Input type="email" value={form.email} required data-testid="docente-email"
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Contraseña</Label>
                <Input type="password" value={form.password} required data-testid="docente-password"
                       onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.telefono} data-testid="docente-telefono"
                       onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cédula</Label>
                <Input value={form.cedula} data-testid="docente-cedula"
                       onChange={(e) => setForm({ ...form, cedula: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Título o especialidad</Label>
                <Input value={form.titulo} placeholder="Ej.: Licenciada en Educación Inicial"
                       data-testid="docente-titulo"
                       onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Materias o grados que imparte</Label>
                <Input value={form.materias} placeholder="Ej.: Matemática de 3ro y 4to"
                       data-testid="docente-materias"
                       onChange={(e) => setForm({ ...form, materias: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Mensaje para la coordinación (opcional)</Label>
                <Textarea rows={4} value={form.mensaje} data-testid="docente-mensaje"
                          onChange={(e) => setForm({ ...form, mensaje: e.target.value })} />
              </div>
              {error && (
                <p className="sm:col-span-2 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive"
                   data-testid="docente-registro-error">{error}</p>
              )}
              <Button type="submit" disabled={busy} className="sm:col-span-2 rounded-full py-6 text-base"
                      data-testid="enviar-registro-docente">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar solicitud de registro"}
              </Button>
              <p className="sm:col-span-2 text-center text-xs text-muted-foreground">
                ¿Ya tiene cuenta? <Link to="/login" className="text-primary hover:underline">Inicie sesión aquí</Link>
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
